import { hasAnalyticsConsent } from "@/lib/privacyConsent";
import {
  getRollbar,
  isRollbarConfiguredForApp,
  triggerRollbarReplay,
} from "@/lib/rollbar";

type RollbarLevel = "debug" | "info" | "warning" | "error" | "critical";

export interface RollbarReportOptions {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: RollbarLevel;
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (error && typeof error === "object" && "message" in error) {
    return new Error(String((error as Record<string, unknown>).message));
  }
  return new Error(String(error));
}

function shouldSend(): boolean {
  return isRollbarConfiguredForApp() && hasAnalyticsConsent();
}

export function reportToRollbar(
  error: unknown,
  options: RollbarReportOptions = {},
): void {
  if (!shouldSend()) return;

  const rollbar = getRollbar();
  if (!rollbar) return;

  const finalError = toError(error);
  const payload = {
    custom: {
      tags: options.tags ?? {},
      ...(options.extra ?? {}),
    },
  };

  switch (options.level ?? "error") {
    case "debug":
      rollbar.debug(finalError, payload);
      break;
    case "info":
      rollbar.info(finalError, payload);
      break;
    case "warning":
      rollbar.warning(finalError, payload);
      break;
    case "critical":
      rollbar.critical(finalError, payload);
      break;
    case "error":
    default:
      rollbar.error(finalError, payload);
      break;
  }
}

export function captureRollbarEvent(
  name: string,
  properties: Record<string, unknown> = {},
): void {
  if (!shouldSend()) return;

  const rollbar = getRollbar();
  if (!rollbar) return;

  rollbar.captureEvent(
    {
      type: "manual",
      source: "client",
      body: {
        message: name,
        ...properties,
      },
    },
    "info",
  );
}

export function sendRollbarTestError(): void {
  const error = new Error("Rollbar test error from Office Rhythm");
  reportToRollbar(error, {
    tags: { test: "rollbar", source: "settings" },
    extra: { replay_expected: true },
  });
  triggerRollbarReplay({ reason: "manual_test_error" });
}

export function throwUncaughtRollbarTestError(): void {
  window.setTimeout(() => {
    throw new Error("Rollbar uncaught test error from Office Rhythm");
  }, 0);
}
